import { useRef, useState } from 'react';
import { Camera, Loader2, X, Check, Trash2, Wand2, Image as ImageIcon, Smartphone, Printer, ExternalLink, RefreshCw, Video, VideoOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { enhanceDocument } from '@/lib/documentEnhancer';
import MobileUploadQR from './MobileUploadQR';

// Local companion bridge — a small Python app we distribute for Mac/Windows
// that talks to network scanners via SANE / eSCL / TWAIN and exposes a
// tiny HTTP endpoint the browser can reach via localhost.  We probe it
// with a very short fetch timeout so the UI stays responsive even when
// the bridge isn't installed.
const SCANNER_BRIDGE_PROBE_URL = 'http://localhost:9977/health';
const SCANNER_BRIDGE_TIMEOUT_MS = 700;

/**
 * ScannerCaptureButton — turns the phone/tablet camera into a document scanner.
 *
 * Why this exists:
 *   In the clinic, most incoming paper (blood tests, radiology reports,
 *   consent forms, IDs) doesn't sit in front of a flat-bed scanner —
 *   it lives on the receptionist's desk or the surgeon's iPad.  Rather
 *   than photographing → emailing → downloading → uploading, this button
 *   lets the user snap N pages with the rear camera and produces ONE
 *   consolidated PDF that gets uploaded exactly like a scan.
 *
 * How it works:
 *   1. On tap it opens a native camera capture via <input capture="environment">.
 *   2. Each capture appears as a thumbnail with delete/reorder controls
 *      inside a review modal.
 *   3. "Guardar como PDF" flattens all pages into A4 (portrait), preserving
 *      aspect via `img.getImageProperties()`, and hands the resulting
 *      `File` back to the parent through `onScan(file)`.
 *
 * The parent decides where the PDF ends up (POST /api/test-files/upload,
 * embed as attachment in a surgical report, etc.) — this component only
 * captures + composes.  Keeps it reusable across the 3 target flows:
 *   • Files tab (TestFileUploader)
 *   • Surgical report form
 *   • Clinical note form
 *
 * On desktop where there's no rear camera, the same input opens the OS
 * file picker (which can point at any scanner-driver output folder), so
 * the flow degrades gracefully.
 */
export default function ScannerCaptureButton({
  onScan,
  onScanFromMobile,      // optional callback; when provided, adds "Móvil" option
  patientId,             // when present enables the built-in "desde el móvil" QR flow
  patientName,
  clinicalNoteId,        // opcional: preselecciona la nota clínica en el móvil
  label = 'Escanear',
  filenamePrefix = 'escaneo',
  disabled = false,
  size = 'sm',
  variant = 'outline',
  className = '',
  'data-testid': testId = 'scanner-capture-btn',
}) {
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [pages, setPages] = useState([]); // [{ id, originalDataUrl, enhancedDataUrl, useEnhanced, enhancing, name }]
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Source-selector dialog: patient chooses cámara / móvil / escáner de red.
  const [sourceOpen, setSourceOpen] = useState(false);
  // Mobile QR pairing modal — only used when `patientId` is provided.
  const [mobileQrOpen, setMobileQrOpen] = useState(false);
  // Live webcam capture modal (getUserMedia).  We prefer this over the
  // native <input capture> because on desktop Safari/Chrome the capture
  // hint is silently ignored and the browser falls back to the OS file
  // picker — which is exactly the bug the user reported.
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [webcamStarting, setWebcamStarting] = useState(false);
  const [webcamError, setWebcamError] = useState('');
  const [webcamFacing, setWebcamFacing] = useState('environment'); // environment | user
  // Bridge detection + install prompt state.
  const [bridgeState, setBridgeState] = useState('unknown'); // unknown | present | missing | scanning
  const [installOpen, setInstallOpen] = useState(false);
  const enableMobileOption = Boolean(patientId) || typeof onScanFromMobile === 'function';

  // NOTE: we deliberately do NOT warm-up OpenCV/jscanify on mount.  The
  // clinical forms (surgical report + evolution note) mount up to 4
  // instances of this button, and each one loading ~9.5 MB of OpenCV
  // WASM was blocking the main thread for several seconds on production
  // — Chrome showed "La página no responde" and users had to reload.
  // OpenCV is now loaded lazily inside `enhanceDocument` the first time
  // the user actually captures a page.  Same for jsPDF (dynamic import
  // in handleSavePdf below).

  const openSourceSelector = () => {
    setSourceOpen(true);
    // Kick off a fast bridge probe in the background so by the time the
    // user hovers the "Escáner de red" option we already know whether
    // the companion utility is installed on this machine.
    probeBridge();
  };

  const openCamera = async () => {
    setSourceOpen(false);
    setWebcamError('');
    // Try to start a live camera stream via getUserMedia.  If not
    // available (e.g. no camera, permissions denied, HTTP not secure),
    // fall back to the native file picker so we don't leave the user
    // with a dead button.
    if (!navigator.mediaDevices?.getUserMedia) {
      openFilePickerFallback();
      return;
    }
    setWebcamOpen(true);
    setWebcamStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: webcamFacing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      // Wait a tick so the <video> ref exists after the modal renders.
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (e) {
      // Common failures on desktop Mac: no camera (NotFoundError), user
      // rejected permission (NotAllowedError).  Both cases we degrade
      // to the file picker so the doctor isn't stuck.
      const msg = e?.name === 'NotAllowedError'
        ? 'Permite el acceso a la cámara desde el candado de la barra de direcciones y vuelve a intentarlo.'
        : e?.name === 'NotFoundError'
        ? 'No hemos encontrado ninguna cámara en este dispositivo. Puedes subir un archivo en su lugar.'
        : 'No se pudo abrir la cámara. Puedes subir un archivo en su lugar.';
      setWebcamError(msg);
    } finally {
      setWebcamStarting(false);
    }
  };

  const openFilePickerFallback = () => {
    // Legacy path — only used when getUserMedia is unavailable.
    setWebcamOpen(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.click();
      }
    }, 60);
  };

  const stopWebcam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setWebcamOpen(false);
    setWebcamError('');
  };

  const flipWebcam = async () => {
    const next = webcamFacing === 'environment' ? 'user' : 'environment';
    setWebcamFacing(next);
    // Restart stream on new camera.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setWebcamStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: next }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch (_) {
      // ignore — the previous stream is already dead so the modal will
      // show the "cámara no disponible" state.
    } finally {
      setWebcamStarting(false);
    }
  };

  const captureFromWebcam = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    // Draw the current frame to a canvas at the native resolution so we
    // keep every pixel for the auto-crop pass.
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    // Convert to File so we can reuse the exact same "add + enhance +
    // review" pipeline the file-picker path uses.
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], `${filenamePrefix}-${Date.now()}.jpg`, { type: 'image/jpeg' });
    stopWebcam();
    // Reuse existing multi-file handler as if the user had picked one.
    await handleFilesSelected({ target: { files: [file] } });
  };

  // Fire-and-forget probe of the local companion bridge.  We use
  // AbortController with a short timeout so the UI never has to wait.
  const probeBridge = async () => {
    if (bridgeState === 'scanning' || bridgeState === 'present') return;
    setBridgeState('scanning');
    const ac = new AbortController();
    const to = setTimeout(() => ac.abort(), SCANNER_BRIDGE_TIMEOUT_MS);
    try {
      const r = await fetch(SCANNER_BRIDGE_PROBE_URL, { signal: ac.signal, mode: 'cors' });
      clearTimeout(to);
      setBridgeState(r.ok ? 'present' : 'missing');
    } catch {
      clearTimeout(to);
      setBridgeState('missing');
    }
  };

  const handlePickMobile = () => {
    setSourceOpen(false);
    if (typeof onScanFromMobile === 'function') {
      // Parent-managed QR flow (used by GalleryHeader → reuses the
      // page-level MobileUploadQR modal so we don't duplicate state).
      onScanFromMobile();
      return;
    }
    if (patientId) {
      // Self-managed QR flow — pop our own MobileUploadQR bound to the
      // patient the form is editing.  The doctor scans, and the
      // resulting file lands in the patient's Docs like any other
      // mobile upload.  No extra plumbing needed on the parent form.
      setMobileQrOpen(true);
    }
  };

  const handlePickNetworkScanner = () => {
    if (bridgeState === 'present') {
      // TODO(bridge): call http://localhost:9977/scan and hand the returned
      // PDF/PNG straight into `onScan` like the camera flow does.  Left
      // stubbed on purpose — the wire format is defined by the bridge app
      // which is still in development for Mac/Windows.
      toast.info('Utilidad detectada. La captura desde escáner llegará en la próxima actualización de la utilidad.');
      setSourceOpen(false);
      return;
    }
    // Not installed → show the install helper.
    setSourceOpen(false);
    setInstallOpen(true);
  };

  const handleFilesSelected = async (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    // Cap at 20 pages per document to keep PDF sizes reasonable
    // and prevent memory issues on older iPads.
    const remaining = 20 - pages.length;
    if (remaining <= 0) {
      toast.error('Máximo 20 páginas por documento');
      return;
    }
    const toAdd = list.slice(0, remaining);
    // Read each file into a data URL and add the page immediately with
    // enhancing=true. The enhancement itself runs asynchronously and
    // updates the page in-place when done — so the user sees the raw
    // thumbnail first (fast feedback) and it swaps to the enhanced
    // version a moment later.
    const initial = await Promise.all(
      toAdd.map(
        (f) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = async () => {
              // Defensive HEIC normalisation — iOS Safari can hand back raw
              // HEIC when the user opts out of the "Most Compatible" camera
              // format.  jsPDF.addImage silently rejects HEIC, so we
              // re-encode via canvas.  For everything else (JPEG/PNG/WebP)
              // we skip this cost.
              const isHeic = /\.(heic|heif)$/i.test(f.name) || /^image\/hei[cf]$/i.test(f.type);
              let dataUrl = reader.result;
              if (isHeic) {
                try {
                  dataUrl = await new Promise((res, rej) => {
                    const img = new Image();
                    img.onload = () => {
                      const cv = document.createElement('canvas');
                      cv.width = img.naturalWidth;
                      cv.height = img.naturalHeight;
                      cv.getContext('2d').drawImage(img, 0, 0);
                      res(cv.toDataURL('image/jpeg', 0.9));
                    };
                    img.onerror = () => rej(new Error('HEIC decode failed'));
                    img.src = reader.result;
                  });
                } catch {
                  // If the browser can't even decode HEIC into an <img>
                  // (older Chromium), skip this file rather than upload
                  // something jsPDF will reject downstream.
                  return resolve(null);
                }
              }
              resolve({
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                originalDataUrl: dataUrl,
                enhancedDataUrl: null,
                useEnhanced: true,
                enhancing: true,
                name: f.name,
              });
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(f);
          })
      )
    );
    const validPages = initial.filter(Boolean);
    setPages((prev) => [...prev, ...validPages]);
    if (!reviewOpen) setReviewOpen(true);
    // reset the input so the same file can be picked again next time
    if (inputRef.current) inputRef.current.value = '';

    // Fire the enhancement passes.  Sequential (not Promise.all) because
    // OpenCV.js is single-threaded WASM and parallel calls just fight
    // for the same runtime lock.
    for (const p of validPages) {
      const result = await enhanceDocument(p.originalDataUrl);
      setPages((prev) =>
        prev.map((x) =>
          x.id === p.id
            ? {
                ...x,
                enhancedDataUrl: result.enhanced ? result.dataUrl : null,
                useEnhanced: result.enhanced, // fall back to original if enhancement flagged failure
                enhancing: false,
              }
            : x
        )
      );
    }
  };

  const removePage = (id) => setPages((p) => p.filter((x) => x.id !== id));

  const toggleEnhanced = (id) => {
    setPages((p) => p.map((x) => (x.id === id ? { ...x, useEnhanced: !x.useEnhanced } : x)));
  };

  const cancel = () => {
    setPages([]);
    setReviewOpen(false);
  };

  const handleSavePdf = async () => {
    if (pages.length === 0) return;
    setSaving(true);
    try {
      // Dynamic import so jsPDF is only downloaded when the user actually
      // saves a scan.  Keeps the clinical form initial-render cost close
      // to zero for the majority of visits that don't use the scanner.
      const { default: jsPDF } = await import('jspdf');
      // A4 portrait, in mm.  jsPDF handles the physical page geometry;
      // for each captured image we fit-inside the printable box
      // (297mm × 210mm minus a small margin) while preserving aspect.
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();
        const p = pages[i];
        const dataUrl = (p.useEnhanced && p.enhancedDataUrl) ? p.enhancedDataUrl : p.originalDataUrl;
        // getImageProperties returns width/height in px, we just need
        // the ratio to fit the image into the A4 printable box.
        const props = pdf.getImageProperties(dataUrl);
        const ratio = props.width / props.height;
        let w = maxW;
        let h = w / ratio;
        if (h > maxH) {
          h = maxH;
          w = h * ratio;
        }
        const x = (pageW - w) / 2;
        const y = (pageH - h) / 2;
        // JPEG for size; the source is likely a phone photo so JPEG
        // is what we get anyway.  addImage auto-detects the source
        // encoding from the data URL prefix.
        pdf.addImage(dataUrl, undefined, x, y, w, h, undefined, 'FAST');
      }

      // Serialize to a Blob → File so the caller can treat it exactly
      // like a <input type="file"> selection.
      const blob = pdf.output('blob');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const file = new File([blob], `${filenamePrefix}-${stamp}.pdf`, {
        type: 'application/pdf',
      });

      await Promise.resolve(onScan?.(file));
      setPages([]);
      setReviewOpen(false);
      toast.success(`PDF con ${pages.length} página${pages.length === 1 ? '' : 's'} listo`);
    } catch (err) {
      console.error('Scan-to-PDF failed:', err);
      toast.error('No se pudo generar el PDF');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        size={size}
        variant={variant}
        onClick={openSourceSelector}
        disabled={disabled}
        className={`gap-1.5 ${className}`}
        data-testid={testId}
      >
        <Camera className="w-4 h-4" />
        {label}
      </Button>

      {/* Source selector — patient picks between the device camera, their
          phone (QR pairing) or the network scanner via the companion app. */}
      <Dialog open={sourceOpen} onOpenChange={setSourceOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`${testId}-source-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-brand-deep" />
              ¿Desde dónde quieres escanear?
            </DialogTitle>
            <DialogDescription>
              Elige el origen del documento. Todo termina en un único PDF que se adjunta a la ficha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button
              type="button"
              onClick={openCamera}
              className="w-full flex items-start gap-3 rounded-xl border border-slate-200 p-3 hover:bg-brand-soft/30 hover:border-brand-medium transition-colors text-left"
              data-testid={`${testId}-source-camera`}
            >
              <div className="w-10 h-10 rounded-lg bg-brand-soft/60 flex items-center justify-center flex-shrink-0">
                <Camera className="w-5 h-5 text-brand-deep" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-brand-navy">Con la cámara de este dispositivo</div>
                <div className="text-xs text-slate-500 mt-0.5">Captura una o varias páginas con la cámara. Ideal para el iPad o el móvil.</div>
              </div>
            </button>

            {enableMobileOption && (
              <button
                type="button"
                onClick={handlePickMobile}
                className="w-full flex items-start gap-3 rounded-xl border border-slate-200 p-3 hover:bg-brand-soft/30 hover:border-brand-medium transition-colors text-left"
                data-testid={`${testId}-source-mobile`}
              >
                <div className="w-10 h-10 rounded-lg bg-brand-soft/60 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-5 h-5 text-brand-deep" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-brand-navy">Desde mi móvil (escanea un QR)</div>
                  <div className="text-xs text-slate-500 mt-0.5">Ideal cuando el papel no está delante del ordenador. Se emparejará tu móvil con esta ficha.</div>
                </div>
              </button>
            )}

            <button
              type="button"
              onClick={handlePickNetworkScanner}
              className="w-full flex items-start gap-3 rounded-xl border border-slate-200 p-3 hover:bg-brand-soft/30 hover:border-brand-medium transition-colors text-left opacity-80"
              data-testid={`${testId}-source-network`}
            >
              <div className="w-10 h-10 rounded-lg bg-brand-soft/60 flex items-center justify-center flex-shrink-0">
                <Printer className="w-5 h-5 text-brand-deep" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-brand-navy flex items-center gap-1.5">
                  Desde un escáner de red
                  {bridgeState === 'present'
                    ? <span className="text-[10px] text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">Detectado</span>
                    : <span className="text-[10px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">Próximamente</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Requiere una utilidad para Mac / Windows que aún estamos desarrollando. Puedes usar mientras tanto la cámara o el móvil.</div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* "Escáner de red" is still in development — until the .dmg/.exe
          are signed and hosted, we show a short honest note instead of
          dead download buttons that only return 404. */}
      <Dialog open={installOpen} onOpenChange={setInstallOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`${testId}-install-dialog`}>
          <DialogHeader>
            <DialogTitle>Escaneo directo desde el escáner de red</DialogTitle>
            <DialogDescription>
              Esta función necesita una pequeña utilidad para Mac / Windows que aún estamos desarrollando.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <div className="rounded-lg border border-brand-medium/60 bg-brand-soft/30 p-3 text-brand-navy text-xs leading-relaxed">
              Mientras tanto puedes escanear:
              <ul className="list-disc ml-5 mt-1 space-y-0.5">
                <li>Con la <strong>cámara del iPad</strong> o del ordenador (recorte automático incluido).</li>
                <li>Desde tu <strong>móvil</strong>, escaneando el QR y usando la cámara del teléfono.</li>
              </ul>
            </div>
            <a
              href="/api/tools/scanner-bridge/readme"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-deep hover:underline inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Detalles técnicos del proyecto
            </a>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInstallOpen(false)}>Entendido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live webcam capture — used instead of the native <input capture>
          because desktop browsers silently ignore that hint and fall
          back to the file picker. */}
      <Dialog open={webcamOpen} onOpenChange={(o) => { if (!o) stopWebcam(); }}>
        <DialogContent className="sm:max-w-2xl" data-testid={`${testId}-webcam-dialog`}>
          <DialogHeader>
            <DialogTitle>Capturar con la cámara</DialogTitle>
            <DialogDescription>Enfoca el documento y pulsa capturar. Puedes añadir varias páginas.</DialogDescription>
          </DialogHeader>
          <div className="relative rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '4 / 3' }}>
            {webcamError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 text-white bg-slate-900/95">
                <VideoOff className="w-10 h-10 mb-3 text-amber-400" />
                <p className="text-sm font-medium mb-2">Cámara no disponible</p>
                <p className="text-xs text-slate-300 max-w-md">{webcamError}</p>
                <Button variant="outline" size="sm" className="mt-4 text-slate-900" onClick={openFilePickerFallback}>
                  Subir archivo en su lugar
                </Button>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                  data-testid={`${testId}-webcam-video`}
                />
                {webcamStarting && (
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" size="sm" onClick={flipWebcam} disabled={!!webcamError || webcamStarting}>
              <RefreshCw className="w-4 h-4 mr-1.5" /> Cambiar cámara
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={stopWebcam}>Cancelar</Button>
              <Button
                size="sm"
                onClick={captureFromWebcam}
                disabled={!!webcamError || webcamStarting}
                className="bg-brand-deep hover:bg-brand-deeper text-white"
                data-testid={`${testId}-webcam-capture`}
              >
                <Video className="w-4 h-4 mr-1.5" /> Capturar página
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Self-managed mobile QR pairing modal — only used when the form
          passed us a `patientId` so we can generate a mobile-upload
          token bound to it. */}
      {patientId && (
        <MobileUploadQR
          open={mobileQrOpen}
          onOpenChange={setMobileQrOpen}
          patientId={patientId}
          patientName={patientName}
          clinicalNoteId={clinicalNoteId}
        />
      )}

      <Dialog open={reviewOpen} onOpenChange={(o) => { if (!saving) setReviewOpen(o); }}>
        <DialogContent className="sm:max-w-2xl" data-testid={`${testId}-review-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-brand-deep" />
              Documento escaneado ({pages.length} página{pages.length === 1 ? '' : 's'})
            </DialogTitle>
            <DialogDescription>
              Revisa las páginas capturadas. Puedes eliminar las que no
              necesites o añadir más antes de guardar el PDF.
            </DialogDescription>
          </DialogHeader>

          {pages.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              Sin páginas todavía.
            </div>
          ) : (
            <>
              <p className="text-[11px] text-slate-500 -mt-2 mb-1 flex items-center gap-1.5">
                <Wand2 className="w-3 h-3 text-brand-deep" />
                Auto-recorte + corrección de perspectiva activa. Puedes alternar cada página entre &quot;Escaneado&quot; y &quot;Original&quot; con el botón inferior de la miniatura.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto p-1">
                {pages.map((p, idx) => {
                  const displayed = (p.useEnhanced && p.enhancedDataUrl) ? p.enhancedDataUrl : p.originalDataUrl;
                  const canToggle = !!p.enhancedDataUrl && !p.enhancing;
                  return (
                    <div
                      key={p.id}
                      className="relative group aspect-[3/4] bg-slate-100 rounded-lg overflow-hidden border border-slate-200"
                      data-testid={`${testId}-page-${idx}`}
                    >
                      <img src={displayed} alt={`Página ${idx + 1}`} className="w-full h-full object-cover" />
                      <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                        {idx + 1}
                      </span>
                      {/* Enhancement badge — top-right */}
                      {p.enhancing ? (
                        <span className="absolute top-1 right-1 bg-black/60 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                          <Loader2 className="w-2.5 h-2.5 animate-spin" /> mejorando
                        </span>
                      ) : p.enhancedDataUrl ? (
                        <span className={`absolute top-1 right-1 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ${
                          p.useEnhanced
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-700 text-white'
                        }`}>
                          {p.useEnhanced ? (<><Wand2 className="w-2.5 h-2.5" /> escaneado</>) : (<><ImageIcon className="w-2.5 h-2.5" /> original</>)}
                        </span>
                      ) : (
                        <span className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                          sin recorte
                        </span>
                      )}
                      {/* Controls overlay */}
                      <div className="absolute inset-x-0 bottom-0 p-1 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        {canToggle ? (
                          <button
                            type="button"
                            onClick={() => toggleEnhanced(p.id)}
                            className="text-[10px] px-2 py-1 rounded bg-white/90 text-slate-700 hover:bg-white flex items-center gap-1"
                            title={p.useEnhanced ? 'Mostrar foto original' : 'Volver al recorte automático'}
                            data-testid={`${testId}-page-toggle-${idx}`}
                          >
                            {p.useEnhanced ? (<><ImageIcon className="w-3 h-3" /> Ver original</>) : (<><Wand2 className="w-3 h-3" /> Ver escaneado</>)}
                          </button>
                        ) : (
                          <span />
                        )}
                        <button
                          type="button"
                          onClick={() => removePage(p.id)}
                          className="w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                          title="Eliminar página"
                          data-testid={`${testId}-page-remove-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={cancel}
                disabled={saving}
                className="text-slate-500"
              >
                <X className="w-4 h-4 mr-1" /> Descartar todo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openCamera}
                disabled={saving || pages.length >= 20}
                data-testid={`${testId}-add-more`}
              >
                <Camera className="w-4 h-4 mr-1" /> Añadir página
              </Button>
            </div>
            <Button
              onClick={handleSavePdf}
              disabled={saving || pages.length === 0}
              className="bg-brand-deep hover:bg-brand-deeper"
              data-testid={`${testId}-save-pdf`}
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generando…</>
              ) : (
                <><Check className="w-4 h-4 mr-1.5" /> Guardar como PDF</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
